import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import ConferTicketModal from "../ConferTicketModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [conferTicketModalOpen, setConferTicketModalOpen] = useState(false);

	/*
	 * Estados para retorno al ChatBot
	 */
	const [returnBotModalOpen, setReturnBotModalOpen] = useState(false);
	const [returnBotOptions, setReturnBotOptions] = useState([]);
	const [returnBotSubmitting, setReturnBotSubmitting] = useState(false);
	const [returnBotSelectedKey, setReturnBotSelectedKey] = useState(null);

	/*
	 * Estados para cambio de cola
	 */
	const [changeQueueModalOpen, setChangeQueueModalOpen] = useState(false);
	const [queueOptions, setQueueOptions] = useState([]);
	const [changeQueueLoading, setChangeQueueLoading] = useState(false);
	const [changeQueueSubmitting, setChangeQueueSubmitting] = useState(false);
	const [changeQueueSelectedId, setChangeQueueSelectedId] = useState(null);

	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const history = useHistory();

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	/*
	 * ============================================================
	 * RETORNO AL CHATBOT
	 * ============================================================
	 */

	const executeReturnToBot = async destino => {
		try {
			if (returnBotSubmitting) {
				return;
			}

			if (isMounted.current) {
				setReturnBotSubmitting(true);
				setReturnBotSelectedKey(destino);
			}

			let rptaRB = await api.put(`/ticketsrb/${ticket.id}`, {
				destino,
				whatsappId: ticket.whatsappId
			});

			if (rptaRB.data === 1) {
				toast.warn("No fue posible retornar este ticket al ChatBot con la opción seleccionada.");

				if (isMounted.current) {
					setReturnBotSubmitting(false);
					setReturnBotSelectedKey(null);
				}
				return;
			}

			if (isMounted.current) {
				setReturnBotModalOpen(false);
				setReturnBotOptions([]);
				setReturnBotSubmitting(false);
				setReturnBotSelectedKey(null);
			}

			history.push("/tickets");
		} catch (err) {
			console.error("[RB][front] ERROR executeReturnToBot", {
				message: err?.message,
				responseStatus: err?.response?.status,
				responseData: err?.response?.data,
				err
			});

			if (isMounted.current) {
				setReturnBotSubmitting(false);
				setReturnBotSelectedKey(null);
			}

			toastError(err);
		}
	};

	const handleOpenReturnBot = async () => {
		try {
			handleClose();

			if (!ticket.whatsappId) {
				toast.warn(i18n.t("tickets.noWhaforTicket"));
				return;
			}

			const { data } = await api.get("/ticketsrb-options", {
				params: { whatsappId: ticket.whatsappId }
			});

			const options = Array.isArray(data) ? data : [];

			if (options.length === 0) {
				toast.info(i18n.t("tickets.noReturnToBot"));
				return;
			}

			if (options.length === 1) {
				await executeReturnToBot(options[0].key);
				return;
			}

			if (isMounted.current) {
				setReturnBotOptions(options);
				setReturnBotSelectedKey(null);
				setReturnBotSubmitting(false);
				setReturnBotModalOpen(true);
			}
		} catch (err) {
			console.error("[RB][front] ERROR handleOpenReturnBot", {
				message: err?.message,
				responseStatus: err?.response?.status,
				responseData: err?.response?.data,
				err
			});
			toastError(err);
		}
	};

	const handleCloseReturnBotModal = () => {
		if (returnBotSubmitting) {
			return;
		}

		if (isMounted.current) {
			setReturnBotModalOpen(false);
			setReturnBotOptions([]);
			setReturnBotSelectedKey(null);
			setReturnBotSubmitting(false);
		}
	};

	/*
	 * ============================================================
	 * CAMBIO DE COLA
	 * ============================================================
	 */

	const handleOpenChangeQueue = async () => {
		handleClose();

		if (isMounted.current) {
			setQueueOptions([]);
			setChangeQueueSelectedId(null);
			setChangeQueueSubmitting(false);
			setChangeQueueLoading(true);
			setChangeQueueModalOpen(true);
		}

		try {
			const { data } = await api.get("/queue");

			/*
			 * Se soportan estas dos respuestas:
			 *
			 * [
			 *   { id: 1, name: "Ventas" }
			 * ]
			 *
			 * o:
			 *
			 * {
			 *   queues: [
			 *     { id: 1, name: "Ventas" }
			 *   ]
			 * }
			 */
			const queues = Array.isArray(data)
				? data
				: Array.isArray(data?.queues)
				? data.queues
				: [];

			if (queues.length === 0) {
				if (isMounted.current) {
					setChangeQueueModalOpen(false);
					setChangeQueueLoading(false);
				}

				toast.info("No hay colas disponibles.");
				return;
			}

			if (isMounted.current) {
				setQueueOptions(queues);
			}
		} catch (err) {
			console.error("[QUEUE][front] ERROR handleOpenChangeQueue", {
				message: err?.message,
				responseStatus: err?.response?.status,
				responseData: err?.response?.data,
				err
			});

			if (isMounted.current) {
				setChangeQueueModalOpen(false);
			}

			toastError(err);
		} finally {
			if (isMounted.current) {
				setChangeQueueLoading(false);
			}
		}
	};

	const executeChangeQueue = async queueId => {
		if (changeQueueSubmitting || changeQueueLoading) {
			return;
		}

		const selectedQueueId = Number(queueId);
		const currentQueueId = Number(ticket.queueId);

		if (
			!Number.isInteger(selectedQueueId) ||
			selectedQueueId <= 0
		) {
			toast.warn("La cola seleccionada no es válida.");
			return;
		}

		if (
			Number.isInteger(currentQueueId) &&
			currentQueueId === selectedQueueId
		) {
			toast.info("El ticket ya se encuentra en esta cola.");
			return;
		}

		try {
			if (isMounted.current) {
				setChangeQueueSubmitting(true);
				setChangeQueueSelectedId(selectedQueueId);
			}

			const { data } = await api.put(
				`/tickets-change-queue/${ticket.id}`,
				{
					queueId: selectedQueueId
				}
			);

			console.log("[QUEUE][front] Ticket actualizado", data);

			if (isMounted.current) {
				setChangeQueueModalOpen(false);
				setQueueOptions([]);
				setChangeQueueSubmitting(false);
				setChangeQueueSelectedId(null);
			}

			/*
			 * No se navega ni se recarga manualmente.
			 * Inicialmente dejamos que UpdateTicketService emita
			 * la actualización del ticket por socket.
			 */
			toast.success("La cola del ticket fue actualizada.");
		} catch (err) {
			console.error("[QUEUE][front] ERROR executeChangeQueue", {
				ticketId: ticket?.id,
				queueId: selectedQueueId,
				message: err?.message,
				responseStatus: err?.response?.status,
				responseData: err?.response?.data,
				err
			});

			if (isMounted.current) {
				setChangeQueueSubmitting(false);
				setChangeQueueSelectedId(null);
			}

			toastError(err);
		}
	};

	const handleCloseChangeQueueModal = () => {
		if (changeQueueLoading || changeQueueSubmitting) {
			return;
		}

		if (isMounted.current) {
			setChangeQueueModalOpen(false);
			setQueueOptions([]);
			setChangeQueueSelectedId(null);
			setChangeQueueLoading(false);
			setChangeQueueSubmitting(false);
		}
	};

	/*
	 * ============================================================
	 * OTRAS OPCIONES DEL TICKET
	 * ============================================================
	 */

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleOpenConferModal = e => {
		setConferTicketModalOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	const handleCloseConferTicketModal = () => {
		if (isMounted.current) {
			setConferTicketModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				getContentAnchorEl={null}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				<MenuItem onClick={handleOpenTransferModal}>
					{i18n.t("ticketOptionsMenu.transfer")}
				</MenuItem>
				<MenuItem onClick={handleOpenConferModal}>
					{i18n.t("ticketOptionsMenu.confer")}
				</MenuItem>
				<MenuItem onClick={handleOpenReturnBot}>
					{"ChatBot"}
				</MenuItem>
				<MenuItem onClick={handleOpenChangeQueue}>
					{i18n.t("ticketOptionsMenu.changequeue")}
				</MenuItem>
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<MenuItem onClick={handleOpenConfirmationModal}>
							{i18n.t("ticketOptionsMenu.delete")}
						</MenuItem>
					)}
				/>
			</Menu>

			{/* MODAL DE RETORNO AL CHATBOT */}
			<Dialog
				open={returnBotModalOpen}
				onClose={handleCloseReturnBotModal}
				fullWidth
				maxWidth="sm"
				disableBackdropClick={returnBotSubmitting}
				disableEscapeKeyDown={returnBotSubmitting}
			>
				<DialogTitle>
					{returnBotSubmitting
						? "Transfiriendo al ChatBot..."
						: "Selecciona el punto de retorno del ChatBot"}
				</DialogTitle>

				<DialogContent dividers>
					<List>
						{returnBotOptions.map((item, index) => (
							<ListItem
								button
								key={`${item.key}-${index}`}
								disabled={returnBotSubmitting}
								onClick={() => executeReturnToBot(item.key)}
							>
								<ListItemText
									primary={item.title || item.key}
									secondary={
										returnBotSubmitting && returnBotSelectedKey === item.key
											? "Procesando transferencia..."
											: item.key
									}
								/>
								{returnBotSubmitting && returnBotSelectedKey === item.key && (
									<CircularProgress size={20} />
								)}
							</ListItem>
						))}
					</List>
				</DialogContent>

				<DialogActions>
					<Button
						onClick={handleCloseReturnBotModal}
						color="primary"
						disabled={returnBotSubmitting}
					>
						Cancelar
					</Button>
				</DialogActions>
			</Dialog>

			{/* MODAL DE CAMBIO DE COLA */}
			<Dialog
				open={changeQueueModalOpen}
				onClose={handleCloseChangeQueueModal}
				fullWidth
				maxWidth="sm"
				disableBackdropClick={
					changeQueueLoading || changeQueueSubmitting
				}
				disableEscapeKeyDown={
					changeQueueLoading || changeQueueSubmitting
				}
			>
				<DialogTitle>
					{changeQueueLoading
						? "Consultando colas..."
						: changeQueueSubmitting
						? "Cambiando cola..."
						: "Selecciona la nueva cola"}
				</DialogTitle>

				<DialogContent dividers>
					{changeQueueLoading ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								padding: 30
							}}
						>
							<CircularProgress size={28} />
						</div>
					) : (
						<List>
							{queueOptions.map(queue => {
								const isCurrentQueue =
									Number(queue.id) === Number(ticket.queueId);

								const isSelectedQueue =
									changeQueueSubmitting &&
									Number(changeQueueSelectedId) ===
										Number(queue.id);

								return (
									<ListItem
										button
										key={queue.id}
										disabled={
											changeQueueSubmitting ||
											isCurrentQueue
										}
										onClick={() =>
											executeChangeQueue(queue.id)
										}
									>
										<ListItemText
											primary={
												queue.name ||
												`Cola ${queue.id}`
											}
											secondary={
												isSelectedQueue
													? "Procesando cambio de cola..."
													: isCurrentQueue
													? "Cola actual"
													: null
											}
										/>

										{isSelectedQueue && (
											<CircularProgress size={20} />
										)}
									</ListItem>
								);
							})}
						</List>
					)}
				</DialogContent>

				<DialogActions>
					<Button
						onClick={handleCloseChangeQueueModal}
						color="primary"
						disabled={
							changeQueueLoading || changeQueueSubmitting
						}
					>
						Cancelar
					</Button>
				</DialogActions>
			</Dialog>

			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					ticket.contact.name
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				userId={user.id}
			/>

			<ConferTicketModal
				modalOpen={conferTicketModalOpen}
				onClose={handleCloseConferTicketModal}
				ticketid={ticket.id}
				userId={ticket.userId}
			/>
		</>
	);
};

export default TicketOptionsMenu;